/**
 * A titled board section that ends in a bottom line — the deck's slide grammar.
 *
 * Each slide of the quarterly security deck carries a numbered title, a lead
 * that says what the slide is about, its cards, and then a `bottom_bar:` — one
 * line stating the so-what of everything above it. A reader who takes nothing
 * else away takes that line.
 *
 * The board mirrors it so someone can move between deck and board without
 * re-orienting: same order, same section names, same closing sentence. The
 * bottom line is a required prop rather than an optional one, because a section
 * whose author could not write it is a section that has not decided what it is
 * for.
 */

import { Typography } from "@norican/react";
import type { ReactNode } from "react";

export interface BoardSectionProps {
  /** Anchor id, so a tile can link straight to the section. */
  id: string;
  /** The section's place in the run of sections — "A", "B", "C". */
  eyebrow?: string;
  /** Section title. Use the deck's own card title where one exists. */
  title: string;
  /** What this section is about, in the deck's voice. */
  lead?: ReactNode;
  /** The panels. */
  children: ReactNode;
  /** The one-line so-what that closes the section (the deck's `bottom_bar:`). */
  bottomLine: ReactNode;
}

export function BoardSection({
  id,
  eyebrow,
  title,
  lead,
  children,
  bottomLine,
}: BoardSectionProps) {
  const headingId = `${id}-title`;

  return (
    <section id={id} aria-labelledby={headingId} className="flex scroll-mt-6 flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-3">
          {eyebrow && (
            <Typography
              variant="caption"
              tone="muted"
              as="span"
              className="font-semibold tracking-wide uppercase"
            >
              {eyebrow}
            </Typography>
          )}
          <Typography variant="heading-3" as="h2" id={headingId}>
            {title}
          </Typography>
        </div>
        {lead && (
          <Typography variant="body-sm" tone="muted">
            {lead}
          </Typography>
        )}
      </div>

      <div className="flex flex-col gap-6">{children}</div>

      {/* The deck's bottom bar: the argument, not a caption. */}
      <div className="border-accent bg-bg-muted rounded-md border-l-4 px-4 py-3">
        <Typography variant="body-sm" className="font-medium">
          {bottomLine}
        </Typography>
      </div>
    </section>
  );
}
